import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { publishStore } from '@dukanti/shared';
import type { PublishStoreInput, PublishStoreResult } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

/**
 * Publish the merchant's store via the `publish_store(uuid, true)` RPC.
 *
 * On success the returned `Store` row is written directly into the
 * React Query cache via `queryClient.setQueryData(['store', storeId], row)`
 * so the dashboard re-renders synchronously — no refetch ladder, no
 * optimistic toggle (F-004 AC8 + Decision 13).
 *
 * ## Cache shape contract (M1 — LOAD-BEARING)
 *
 * `setQueryData(['store', storeId], row)` writes the FULL `Store` row
 * returned by the RPC (`PublishStoreResult` is a type alias for `Store`).
 * The dashboard's initial `useQuery(['store', storeId])` MUST project
 * the same full column list — see `dashboard-route.tsx`:
 *
 * ```ts
 * .select(
 *   'id, owner_id, slug, name, description, locale_default, logo_url, published, ' +
 *   'created_at, updated_at, deleted_at'
 * )
 * ```
 *
 * If the initial-fetch projection and the RPC-return shape ever diverge,
 * the next background refetch (window focus, network reconnect,
 * staleTime expiry) will silently wipe `published` from the cache — and
 * AC8 will pass on the first render and break the moment the user
 * backgrounds the tab. Keep the two projections byte-compatible.
 *
 * ## Error behavior
 *
 * This hook does NOT map errors — it re-throws so the calling dialog
 * can run `mapPublishError(err)` itself and decide how to render the
 * banner. Mirrors the F-003 `use-delete-product` → `delete-product-dialog`
 * split.
 *
 * ## Children invalidation
 *
 * On successful publish we invalidate `productKeys.list(storeId)` so
 * the next read of the products list reflects Decision 1's all-or-
 * nothing `products.published = true` flip. `useUnpublishStore` does
 * NOT invalidate children because unpublish leaves them alone
 * (Decision 10 step 4b).
 */
export function usePublishStore(): UseMutationResult<
  PublishStoreResult,
  unknown,
  PublishStoreInput
> {
  const queryClient = useQueryClient();

  return useMutation<PublishStoreResult, unknown, PublishStoreInput>({
    mutationFn: (input) => publishStore(supabase, input),
    onSuccess: (row, variables) => {
      // M1 contract: write the FULL row into the ['store', storeId] cache
      // so the dashboard re-renders from the same shape the initial query
      // projected.
      queryClient.setQueryData(['store', variables.storeId], row);

      // Decision 1: publish flipped every live product's published flag
      // to true. Invalidate the products list so a subsequent read picks
      // up the new state. We intentionally do NOT touch individual
      // product details — the list refetch is sufficient.
      void queryClient.invalidateQueries({
        queryKey: productKeys.list(variables.storeId),
      });
    },
  });
}
